import { useCallback, useEffect, useState } from "react";
import { Plus, Trash, Play, PauseCircle, Clock, Warning, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  listSchedules,
  createSchedule,
  updateSchedule,
  deleteSchedule,
  runScheduleNow,
  listLinks,
} from "../lib/api";
import { PLATFORMS } from "../lib/constants";

const CADENCE_PRESETS = [
  { label: "Hourly", value: 1 },
  { label: "Every 6 hours", value: 6 },
  { label: "Every 12 hours", value: 12 },
  { label: "Daily", value: 24 },
  { label: "Every 3 days", value: 72 },
  { label: "Weekly", value: 168 },
];

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export default function Schedules() {
  const [schedules, setSchedules] = useState([]);
  const [links, setLinks] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    affiliate_link_id: "",
    cadence_hours: 24,
    platforms: ["google"],
    tone: "energetic",
    audience: "",
    generate_images: false,
    campaign_name_template: "Auto · {date}",
  });

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([listSchedules(), listLinks()]);
      setSchedules(s);
      setLinks(l);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load schedules");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (!form.affiliate_link_id) {
      toast.error("Pick an affiliate link");
      return;
    }
    setSaving(true);
    try {
      await createSchedule(form);
      toast.success("Schedule created");
      setOpen(false);
      setForm({
        affiliate_link_id: "",
        cadence_hours: 24,
        platforms: ["google"],
        tone: "energetic",
        audience: "",
        generate_images: false,
        campaign_name_template: "Auto · {date}",
      });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (s) => {
    try {
      await updateSchedule(s.id, { active: !s.active });
      toast.success(s.active ? "Paused" : "Resumed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this schedule?")) return;
    try {
      await deleteSchedule(id);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const runNow = async (id) => {
    toast.info("Running now…");
    try {
      const updated = await runScheduleNow(id);
      toast.success(
        updated.last_error
          ? `Run finished with error: ${updated.last_error.slice(0, 100)}`
          : `Ran successfully · ad_set ${updated.last_ad_set_id?.slice(0, 8) || "?"}`,
      );
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Run failed");
    }
  };

  const togglePlatform = (p) => {
    setForm((f) => {
      const has = f.platforms.includes(p);
      return {
        ...f,
        platforms: has ? f.platforms.filter((x) => x !== p) : [...f.platforms, p],
      };
    });
  };

  const linkName = (id) => links.find((l) => l.id === id)?.name || id?.slice(0, 8) || "—";

  return (
    <div className="max-w-6xl" data-testid="schedules-page">
      <div className="mb-10 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-mono mb-3">automation</div>
          <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tighter">
            Scheduled <span className="font-black text-[#ccff00]">Auto-Gen</span>
          </h1>
          <p className="text-zinc-500 mt-3 max-w-2xl text-sm">
            Set-and-forget campaigns. Pick an affiliate link and a cadence — we&apos;ll re-scrape and generate fresh ads on your schedule.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="new-schedule-btn"
              className="bg-[#ccff00] text-black hover:bg-[#b3e600] font-semibold py-3 px-5 flex items-center gap-2 transition-colors"
            >
              <Plus size={14} weight="bold" />
              New schedule
            </button>
          </DialogTrigger>
          <DialogContent
            className="bg-[#0a0a0a] border border-white/10 rounded-none max-w-lg"
            data-testid="new-schedule-dialog"
          >
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">
                New schedule
              </DialogTitle>
              <DialogDescription className="text-zinc-500 text-sm">
                Runs in the background — no browser needed.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-3">
              <div>
                <div className="label-mono mb-2">affiliate link</div>
                <Select
                  value={form.affiliate_link_id}
                  onValueChange={(v) => setForm({ ...form, affiliate_link_id: v })}
                >
                  <SelectTrigger
                    data-testid="schedule-link-select"
                    className="bg-[#121212] border-white/10 rounded-none h-10"
                  >
                    <SelectValue placeholder="Pick a link" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10 rounded-none">
                    {links.length === 0 && (
                      <SelectItem disabled value="_none">
                        No links yet — create one in My Links first
                      </SelectItem>
                    )}
                    {links.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="label-mono mb-2">cadence</div>
                <Select
                  value={String(form.cadence_hours)}
                  onValueChange={(v) => setForm({ ...form, cadence_hours: parseInt(v, 10) })}
                >
                  <SelectTrigger
                    data-testid="schedule-cadence-select"
                    className="bg-[#121212] border-white/10 rounded-none h-10"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10 rounded-none">
                    {CADENCE_PRESETS.map((c) => (
                      <SelectItem key={c.value} value={String(c.value)}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="label-mono mb-2">platforms</div>
                <div className="grid grid-cols-3 gap-1.5">
                  {PLATFORMS.map((p) => {
                    const active = form.platforms.includes(p.id);
                    return (
                      <button
                        key={p.id}
                        data-testid={`schedule-platform-${p.id}`}
                        onClick={() => togglePlatform(p.id)}
                        className={`text-xs font-mono uppercase tracking-wider py-1.5 border transition-colors ${
                          active
                            ? "bg-[#ccff00] text-black border-[#ccff00] font-bold"
                            : "bg-transparent text-zinc-400 border-white/10 hover:border-white/30"
                        }`}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="label-mono mb-2">audience (optional)</div>
                <Input
                  data-testid="schedule-audience-input"
                  value={form.audience}
                  onChange={(e) => setForm({ ...form, audience: e.target.value })}
                  placeholder="fitness enthusiasts, 25-45…"
                  className="bg-[#121212] border-white/10 h-10 rounded-none font-mono text-xs"
                />
              </div>

              <div>
                <div className="label-mono mb-2">campaign name template</div>
                <Input
                  data-testid="schedule-name-input"
                  value={form.campaign_name_template}
                  onChange={(e) => setForm({ ...form, campaign_name_template: e.target.value })}
                  placeholder="Auto · {date}"
                  className="bg-[#121212] border-white/10 h-10 rounded-none font-mono text-xs"
                />
                <div className="text-[10px] text-zinc-600 mt-1 font-mono">
                  {"{date}"} → auto-substituted at run time
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  data-testid="schedule-images-checkbox"
                  checked={form.generate_images}
                  onCheckedChange={(v) => setForm({ ...form, generate_images: !!v })}
                />
                <span className="text-sm text-zinc-300">
                  Also generate creative images (slower, uses more credits)
                </span>
              </label>
            </div>
            <DialogFooter>
              <button
                data-testid="schedule-save-btn"
                onClick={create}
                disabled={saving}
                className="bg-[#ccff00] text-black hover:bg-[#b3e600] disabled:opacity-40 font-semibold py-2.5 px-4"
              >
                {saving ? "Saving…" : "Create schedule"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {schedules.length === 0 && (
        <div className="text-center py-16 text-zinc-500 font-mono text-sm">
          No schedules yet. Click <span className="text-[#ccff00]">+ New schedule</span> to automate an affiliate link.
        </div>
      )}

      <div className="space-y-3">
        {schedules.map((s) => (
          <div
            key={s.id}
            data-testid={`schedule-row-${s.id}`}
            className="bg-[#0a0a0a] border border-white/10 hover:border-white/20 p-5 transition-colors"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Clock
                    size={14}
                    weight="fill"
                    className={s.active ? "text-[#ccff00]" : "text-zinc-600"}
                  />
                  <div className="font-display font-bold text-white truncate">
                    {linkName(s.affiliate_link_id)}
                  </div>
                  <span
                    className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 border ${
                      s.active
                        ? "bg-[#ccff00]/10 text-[#ccff00] border-[#ccff00]/30"
                        : "bg-zinc-800 text-zinc-500 border-zinc-700"
                    }`}
                  >
                    {s.active ? "active" : "paused"}
                  </span>
                </div>
                <div className="font-mono text-[10px] text-zinc-500 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span>cadence: every {s.cadence_hours}h</span>
                  <span>platforms: {s.platforms.join(", ")}</span>
                  <span>runs: {s.run_count}</span>
                  <span>next: {formatDate(s.next_run_at)}</span>
                  {s.last_run_at && <span>last: {formatDate(s.last_run_at)}</span>}
                </div>
                {s.last_error && (
                  <div className="mt-2 flex items-start gap-1.5 text-[11px] text-red-400 font-mono">
                    <Warning size={12} weight="fill" className="mt-0.5 flex-shrink-0" />
                    <span className="break-all">{s.last_error}</span>
                  </div>
                )}
                {s.last_ad_set_id && !s.last_error && (
                  <div className="mt-2 flex items-center gap-1.5 text-[11px] text-emerald-400 font-mono">
                    <CheckCircle size={12} weight="fill" />
                    <span>last ad_set: {s.last_ad_set_id.slice(0, 8)}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  data-testid={`run-now-${s.id}`}
                  onClick={() => runNow(s.id)}
                  className="p-2 bg-[#121212] border border-white/10 hover:border-[#ccff00] hover:text-[#ccff00] transition-colors"
                  title="Run now"
                >
                  <Play size={13} weight="fill" />
                </button>
                <button
                  data-testid={`toggle-active-${s.id}`}
                  onClick={() => toggleActive(s)}
                  className="p-2 bg-[#121212] border border-white/10 hover:border-white/30 hover:text-white transition-colors"
                  title={s.active ? "Pause" : "Resume"}
                >
                  <PauseCircle
                    size={13}
                    weight={s.active ? "fill" : "regular"}
                  />
                </button>
                <button
                  data-testid={`delete-schedule-${s.id}`}
                  onClick={() => remove(s.id)}
                  className="p-2 bg-[#121212] border border-white/10 hover:border-red-500 hover:text-red-500 transition-colors"
                  title="Delete"
                >
                  <Trash size={13} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
